import { ChangeEvent, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import styles from "./Messages.module.scss";
import TitleUpdater from "../../components/common/title/TitleUpdater";
import ChatService from "../../api/services/chat.service";
import { ChatMessage, ChatSession } from "../../api/types/chat.types";
import { getCookie } from "../../utils/cookies";

const WS_BASE = process.env.REACT_APP_WS_API_BASE_URL;
const TYPING_TIMEOUT = 2000;
const RECONNECT_DELAY = 3000;

const Messages = () => {
  const { t } = useTranslation("translation");
  const [openSessions, setOpenSessions] = useState<ChatSession[]>([]);
  const [mySessions, setMySessions] = useState<ChatSession[]>([]);
  const [activeSession, setActiveSession] = useState<ChatSession | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [typingStore, setTypingStore] = useState(false);
  const [showSidebar, setShowSidebar] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");

  const wsRef = useRef<WebSocket | null>(null);
  const typingTimeout = useRef<NodeJS.Timeout | null>(null);
  const chatBodyRef = useRef<HTMLDivElement | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const sortedMessages = useMemo(
    () =>
      [...messages].sort(
        (a, b) => new Date(a.created).getTime() - new Date(b.created).getTime(),
      ),
    [messages],
  );

  const filteredMySessions = useMemo(() => {
    if (!searchQuery.trim()) return mySessions;
    const query = searchQuery.toLowerCase();
    return mySessions.filter(
      (s) =>
        s.store?.name?.toLowerCase().includes(query) ||
        s.last_message?.content?.toLowerCase().includes(query),
    );
  }, [mySessions, searchQuery]);

  useEffect(() => {
    refreshLists();
    const ws = createSocket();
    return () => {
      wsRef.current = null;
      ws?.close();
    };
  }, []);

  useEffect(() => {
    const lastIncoming = [...messages]
      .reverse()
      .find((m) => m.sender_type === "store");
    if (activeSession && lastIncoming) {
      ChatService.markRead(activeSession.id).catch(console.error);
    }
  }, [messages, activeSession]);

  useEffect(() => {
    chatBodyRef.current?.scrollTo({
      top: chatBodyRef.current.scrollHeight,
      behavior: "smooth",
    });
  }, [sortedMessages, typingStore]);

  useEffect(() => {
    const handleResize = () => {
      setShowSidebar(window.innerWidth > 768 || !activeSession);
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [activeSession]);

  const refreshLists = async (skipAutoSelect = false) => {
    try {
      const [openRes, myRes] = await Promise.all([
        ChatService.getOpenSessions(),
        ChatService.getMySessions(),
      ]);
      setOpenSessions(openRes.data.data || []);
      setMySessions(myRes.data.data || []);

      if (!skipAutoSelect && !activeSession && myRes.data.data?.length) {
        selectSession(myRes.data.data[0]);
      }
    } catch (error) {
      console.error("Failed to refresh lists:", error);
    }
  };

  const createSocket = () => {
    const token = getCookie("LuxMartAdminDashboardAccessToken");
    if (!token || !WS_BASE) return null;

    const ws = new WebSocket(
      `${WS_BASE}/chat/ws/admin/?token=${encodeURIComponent(token)}`,
    );
    wsRef.current = ws;

    ws.onopen = () => console.log("Chat WebSocket connected");
    ws.onmessage = (event) => {
      try {
        handleSocketEvent(JSON.parse(event.data));
      } catch (error) {
        console.error("WebSocket message parse error:", error);
      }
    };
    ws.onerror = (error) => console.error("Chat WebSocket error:", error);
    ws.onclose = () => {
      console.log("Chat WebSocket closed, reconnecting...");
      wsRef.current = null;
      setTimeout(() => {
        if (!wsRef.current) createSocket();
      }, RECONNECT_DELAY);
    };

    return ws;
  };

  const handleSocketEvent = (payload: any) => {
    if (!payload?.type) return;

    switch (payload.type) {
      case "session_opened":
        if (payload.payload) {
          setOpenSessions((prev) => {
            if (prev.find((p) => p.id === payload.session_id)) return prev;
            return [...prev, payload.payload];
          });
        }
        break;

      case "session_claimed":
      case "session_closed":
        refreshLists();
        if (
          payload.type === "session_closed" &&
          activeSession?.id === payload.session_id
        ) {
          setActiveSession(null);
          setMessages([]);
        }
        break;

      case "message":
        if (payload.payload?.message) {
          const msg = payload.payload.message as ChatMessage;
          if (activeSession?.id === msg.session_id) {
            setMessages((prev) => {
              if (prev.find((m) => m.id === msg.id)) return prev;
              return [...prev, msg];
            });
          } else {
            refreshLists();
          }
        }
        break;

      case "message_read":
        if (activeSession?.id === payload.session_id) {
          setMessages((prev) =>
            prev.map((m) =>
              m.sender_type === "admin" ? { ...m, status: "read" } : m,
            ),
          );
        }
        break;

      case "typing":
        setTypingStore(true);
        if (typingTimeout.current) clearTimeout(typingTimeout.current);
        typingTimeout.current = setTimeout(
          () => setTypingStore(false),
          TYPING_TIMEOUT,
        );
        break;
    }
  };

  const selectSession = async (session: ChatSession) => {
    setActiveSession(session);
    if (window.innerWidth <= 768) setShowSidebar(false);

    try {
      const { data } = await ChatService.getSession(session.id);
      setMessages(data.messages || []);
      setActiveSession(data);
    } catch (error) {
      console.error("Failed to load session:", error);
      setMessages([]);
    }
  };

  const claimSession = async (session: ChatSession) => {
    try {
      const { data } = await ChatService.claim(session.id);
      setActiveSession(data);
      await refreshLists();
      await selectSession(data);
    } catch (error) {
      console.error("Failed to claim session:", error);
    }
  };

  const closeSession = async () => {
    if (!activeSession) return;

    try {
      await ChatService.close(activeSession.id);
      setActiveSession(null);
      setMessages([]);
      refreshLists();
      if (window.innerWidth <= 768) setShowSidebar(true);
    } catch (error) {
      console.error("Failed to close session:", error);
    }
  };

  const handleBack = () => {
    setActiveSession(null);
    setMessages([]);
    setShowSidebar(true);
  };

  const handleSend = async () => {
    if (!activeSession || (!input.trim() && !file)) return;

    const messageText = input.trim();
    setUploading(true);

    try {
      let attachment_url = "";
      let attachment_type = "";

      if (file) {
        const formData = new FormData();
        formData.append("file", file);
        formData.append("session_id", activeSession.id.toString());

        try {
          const uploadRes = await ChatService.upload(formData);
          attachment_url = uploadRes.data.url;
          attachment_type = uploadRes.data.type;
        } catch (error) {
          console.error("File upload failed:", error);
          alert(t("messages.file_upload_failed") as string);
          return;
        }
      }

      const { data } = await ChatService.sendMessage({
        session_id: activeSession.id,
        content: messageText,
        attachment_url,
        attachment_type,
      });

      if (data.message) {
        setMessages((prev) => {
          if (prev.find((m) => m.id === data.message.id)) return prev;
          return [...prev, data.message];
        });
      }

      setActiveSession(data.session || activeSession);
      setInput("");
      setFile(null);
      refreshLists(true);
    } catch (error) {
      console.error("Failed to send message:", error);
      alert(t("messages.failed_to_send") as string);
    } finally {
      setUploading(false);
    }
  };

  const onFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files?.length) setFile(e.target.files[0]);
  };

  const sendTyping = () => {
    if (wsRef.current?.readyState === WebSocket.OPEN && activeSession) {
      wsRef.current.send(
        JSON.stringify({ type: "typing", session_id: activeSession.id }),
      );
    }
  };

  const renderCheckmarks = (status: string) => {
    const isRead = status === "read";
    return (
      <svg
        width="16"
        height="15"
        viewBox="0 0 16 15"
        fill="none"
        className={isRead ? styles.checkRead : styles.checkSent}
      >
        <path
          d="M15.01 3.316l-.478-.372a.365.365 0 0 0-.51.063L8.666 9.879a.32.32 0 0 1-.484.033l-.358-.325a.319.319 0 0 0-.484.032l-.378.483a.418.418 0 0 0 .036.541l1.32 1.266c.143.14.361.125.484-.033l6.272-8.048a.366.366 0 0 0-.064-.512zm-4.1 0l-.478-.372a.365.365 0 0 0-.51.063L4.566 9.879a.32.32 0 0 1-.484.033L1.891 7.769a.366.366 0 0 0-.515.006l-.423.433a.364.364 0 0 0 .006.514l3.258 3.185c.143.14.361.125.484-.033l6.272-8.048a.365.365 0 0 0-.063-.51z"
          fill={isRead ? "#4ade80" : "rgba(255, 255, 255, 0.6)"}
        />
      </svg>
    );
  };

  const renderMessageBody = (msg: ChatMessage) => {
    if (msg.attachment_url) {
      if (msg.attachment_type?.startsWith("image/")) {
        return (
          <div className={styles.imageWrapper}>
            <img
              src={msg.attachment_url}
              alt="attachment"
              className={styles.attachmentImage}
            />
            <div className={styles.imageMeta}>
              <span className={styles.imageTimeText}>
                {new Date(msg.created).toLocaleTimeString([], {
                  hour: "2-digit",
                  minute: "2-digit",
                  hour12: false,
                })}
              </span>
              {msg.sender_type === "admin" && msg.status && (
                <span className={styles.checkmarks}>
                  {renderCheckmarks(msg.status)}
                </span>
              )}
            </div>
          </div>
        );
      }

      return (
        <a
          href={msg.attachment_url}
          target="_blank"
          rel="noreferrer"
          className={styles.attachmentLink}
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            style={{ marginRight: "6px" }}
          >
            <path
              d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          {msg.attachment_url.split("/").pop()}
        </a>
      );
    }

    return <p>{msg.content}</p>;
  };

  const formatTime = (date: string) => {
    const msgDate = new Date(date);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (msgDate.toDateString() === today.toDateString()) {
      return msgDate.toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      });
    }
    if (msgDate.toDateString() === yesterday.toDateString()) {
      return t("messages.yesterday") as string;
    }
    return msgDate.toLocaleDateString([], { month: "short", day: "numeric" });
  };

  return (
    <div className={styles.container}>
      <TitleUpdater title={t("messages.title") as string} />

      {showSidebar && (
        <aside className={styles.sidebar}>
          <input
            type="text"
            placeholder={t("messages.search_placeholder") as string}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={{
              width: "100%",
              padding: "12px 16px",
              borderRadius: "16px",
              border: "1px solid rgba(99, 102, 241, 0.2)",
              background: "rgba(15, 27, 61, 0.6)",
              color: "#ffffff",
              fontSize: "14px",
              outline: "none",
              marginBottom: "12px",
            }}
          />

          <h3>
            {t("messages.open_requests") as string}{" "}
            {openSessions.length > 0 && `(${openSessions.length})`}
          </h3>
          <div className={styles.list}>
            {openSessions.map((s) => (
              <div key={s.id} className={styles.listItem}>
                <div className={styles.storeInfo}>
                  {s.store?.logo_url && (
                    <img
                      src={s.store.logo_url}
                      alt={s.store.name}
                      className={styles.storeLogo}
                    />
                  )}
                  <div>
                    <strong>{s.store?.name || `Store #${s.store_id}`}</strong>
                    {s.last_message && (
                      <p className={styles.preview}>{s.last_message.content}</p>
                    )}
                  </div>
                </div>
                <button onClick={() => claimSession(s)}>
                  {t("messages.answer") as string}
                </button>
              </div>
            ))}
            {!openSessions.length && (
              <p className={styles.empty}>
                {t("messages.no_open_requests") as string}
              </p>
            )}
          </div>

          <h3>
            {t("messages.your_active_chats") as string}{" "}
            {filteredMySessions.length > 0 && `(${filteredMySessions.length})`}
          </h3>
          <div className={styles.list}>
            {filteredMySessions.map((s) => (
              <div
                key={s.id}
                className={`${styles.listItem} ${
                  activeSession?.id === s.id ? styles.activeItem : ""
                }`}
                onClick={() => selectSession(s)}
              >
                <div className={styles.storeInfo}>
                  {s.store?.logo_url && (
                    <img
                      src={s.store.logo_url}
                      alt={s.store.name}
                      className={styles.storeLogo}
                    />
                  )}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        gap: "8px",
                      }}
                    >
                      <strong style={{ flex: 1, minWidth: 0 }}>
                        {s.store?.name || `Store #${s.store_id}`}
                      </strong>
                      {s.last_message && (
                        <span
                          style={{
                            fontSize: "11px",
                            color: "rgba(255, 255, 255, 0.5)",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {formatTime(s.last_message.created)}
                        </span>
                      )}
                    </div>
                    {s.last_message && (
                      <p className={styles.preview}>{s.last_message.content}</p>
                    )}
                  </div>
                </div>
              </div>
            ))}
            {!filteredMySessions.length && (
              <p className={styles.empty}>
                {searchQuery
                  ? (t("messages.no_chats_found") as string)
                  : (t("messages.no_active_chats") as string)}
              </p>
            )}
          </div>
        </aside>
      )}

      {(!showSidebar || window.innerWidth > 768) && (
        <section className={styles.chat}>
          <header className={styles.chatHeader}>
            {window.innerWidth <= 768 && activeSession && (
              <button className={styles.backBtn} onClick={handleBack}>
                <svg viewBox="0 0 24 24" fill="none">
                  <path
                    d="M15 18l-6-6 6-6"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
            )}
            <div className={styles.headerInfo}>
              {activeSession?.store?.logo_url && (
                <img
                  src={activeSession.store.logo_url}
                  alt={activeSession.store.name}
                  className={styles.headerLogo}
                />
              )}
              <div>
                <h3>
                  {activeSession
                    ? activeSession.store?.name ||
                      `Store #${activeSession.store_id}`
                    : (t("messages.select_chat") as string)}
                </h3>
                {activeSession && typingStore && (
                  <span
                    style={{
                      fontSize: "12px",
                      color: "rgba(102, 126, 234, 0.8)",
                      fontStyle: "italic",
                    }}
                  >
                    {t("messages.typing") as string}
                  </span>
                )}
              </div>
            </div>
            {activeSession && (
              <button className={styles.closeBtn} onClick={closeSession}>
                {t("messages.close") as string}
              </button>
            )}
          </header>

          <div className={styles.chatBody} ref={chatBodyRef}>
            {activeSession ? (
              sortedMessages.map((msg) => (
                <div
                  key={msg.id}
                  className={`${styles.message} ${
                    msg.sender_type === "store" ? styles.store : styles.admin
                  }`}
                >
                  {renderMessageBody(msg)}
                  {!msg.attachment_url && (
                    <div className={styles.textMeta}>
                      <span className={styles.textTime}>
                        {new Date(msg.created).toLocaleTimeString([], {
                          hour: "2-digit",
                          minute: "2-digit",
                          hour12: false,
                        })}
                      </span>
                      {msg.sender_type === "admin" && msg.status && (
                        <span className={styles.checkmarks}>
                          {renderCheckmarks(msg.status)}
                        </span>
                      )}
                    </div>
                  )}
                </div>
              ))
            ) : (
              <div className={styles.empty}>
                <div style={{ fontSize: "48px", marginBottom: "16px" }}>💬</div>
                <div>{t("messages.pick_chat") as string}</div>
              </div>
            )}
            {typingStore && activeSession && (
              <div className={styles.typingIndicator}>
                <span>●</span> <span>●</span> <span>●</span>{" "}
                {t("messages.store_typing") as string}
              </div>
            )}
          </div>

          {activeSession && (
            <footer className={styles.chatFooter}>
              {!file && (
                <button
                  className={styles.attachBtn}
                  onClick={() => fileInputRef.current?.click()}
                  title={t("messages.attach_file") as string}
                  disabled={uploading}
                >
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <path
                      d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>
              )}

              <input
                ref={fileInputRef}
                type="file"
                onChange={onFileChange}
                style={{ display: "none" }}
              />

              {file ? (
                <div className={styles.filePreview}>
                  <div className={styles.fileInfo}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                      <path
                        d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                    <span className={styles.fileName}>{file.name}</span>
                  </div>
                  <button
                    onClick={() => setFile(null)}
                    className={styles.removeFileBtn}
                    title={t("messages.remove_file") as string}
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                      <path
                        d="M18 6L6 18M6 6l12 12"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </button>
                </div>
              ) : (
                <input
                  type="text"
                  placeholder={t("messages.type_message") as string}
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) =>
                    e.key === "Enter" && !e.shiftKey && handleSend()
                  }
                  onFocus={sendTyping}
                  onInput={sendTyping}
                  disabled={uploading}
                  className={styles.messageInput}
                />
              )}

              {(input.trim() || file) && (
                <button
                  onClick={handleSend}
                  disabled={uploading}
                  className={styles.sendBtn}
                  title={t("messages.send_message") as string}
                >
                  {uploading ? (
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                      <circle
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="2"
                        opacity="0.3"
                      />
                      <path
                        d="M12 2a10 10 0 0 1 10 10"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                      >
                        <animateTransform
                          attributeName="transform"
                          type="rotate"
                          from="0 12 12"
                          to="360 12 12"
                          dur="1s"
                          repeatCount="indefinite"
                        />
                      </path>
                    </svg>
                  ) : (
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                      <path
                        d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z"
                        fill="currentColor"
                      />
                    </svg>
                  )}
                </button>
              )}
            </footer>
          )}
        </section>
      )}
    </div>
  );
};

export default Messages;
